import React, { ReactNode, useState, useEffect } from 'react';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { cn } from '../../utils/cn';

interface AppShellProps {
    children: ReactNode;
}

export default function AppShell({ children }: AppShellProps) {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200 print:bg-white print:min-h-0">
            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm lg:hidden transition-opacity print:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            <div className="flex h-screen overflow-hidden print:h-auto print:block print:overflow-visible">
                {/* Sidebar */}
                <aside className={cn(
                    "fixed lg:static inset-y-0 left-0 z-50 transition-transform duration-300 lg:translate-x-0 bg-white dark:bg-[#1e293b] border-r border-gray-200 dark:border-gray-700/50 shadow-xl lg:shadow-none print:hidden",
                    isSidebarOpen ? "translate-x-0" : "-translate-x-full"
                )}>
                    <Sidebar />
                </aside>

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden print:overflow-visible print:block">
                    {/* Header */}
                    <div className="print:hidden">
                        <Header
                            onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
                            showMenuButton={true}
                        />
                    </div>

                    {/* Content Scroll Area */}
                    <main className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar relative print:overflow-visible print:block print:h-auto">
                        {!isOnline && (
                            <div className="sticky top-0 z-40 mb-4 bg-error-500 text-white p-4 rounded-xl shadow-lg flex items-center justify-between glass border-error-400/30 animate-fadeInUp">
                                <span className="flex items-center gap-2 font-medium">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    You are currently offline. Some features may be unavailable.
                                </span>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="bg-white/20 hover:bg-white/30 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Retry
                                </button>
                            </div>
                        )}

                        {/* Content Container — pages can opt OUT of the
                            1440px cap by rendering a root element with
                            the `.full-bleed` class. CSS :has() picks
                            that up and removes the max-width so the
                            content can extend to the right edge. */}
                        <div className="mx-auto max-w-[1440px] animate-fadeIn print:max-w-none print:m-0 print:p-0 [&:has(.full-bleed)]:max-w-none [&:has(.full-bleed)]:mx-0">
                            {children}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}
